import _ from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { browserHistory } from 'react-router';
import { ajaxRequestCompleted, ajaxRequestInitiated, genericB2BAccountRequest, saveDPMRequests } from '../../../../redux/actions';
import countries from '../../../common/countries';
import GenericOverlayDialog from '../../../dialogs/genericOverlayDialog';
import { routes } from '../../../../redux/api/routes';
import { sendRequest } from '../../../../redux/api/sendRequest';
import AddressVerificationDialog from '../../../dialogs/addressVerificationDialog';
import { additionalAddressValidationErrorCodesForDpm, addressValidationErrorCode } from '../../../../redux/util/addressValidationErrorCodes';

const US_CONTIGUOUS_STATES = countries.find(country => country.code === 'US').regions.filter(region => !['AK', 'HI'].includes(region.code));

const ADDRESS_VALIDATION_ERROR_CODES = { ...addressValidationErrorCode, ...additionalAddressValidationErrorCodesForDpm };
const SHIPPING_ADDRESS_VALIDATION_ERRORS = Object.entries(ADDRESS_VALIDATION_ERROR_CODES)
	.filter(entry => !['AE08', 'AE09', 'AE17'].includes(entry[0]))
	.map(error => ({ code: error[0], message: error[1] }));
const BILLING_ADDRESS_VALIDATION_ERRORS = Object.entries(ADDRESS_VALIDATION_ERROR_CODES)
	.filter(entry => !['AE17'].includes(entry[0]))
	.map(error => ({ code: error[0], message: error[1] }));
const ALLOWED_BILLING_ADDRESS_VALIDATION_ERROR_CODES = ['AE08', 'AE09'];

const convertMuleToSuggestedAddress = address => ({
	addressline1: address.ADDRESS_LINE1,
	addressline2: address.ADDRESS_LINE2,
	city: address.CITY,
	county: address.COUNTY,
	state: address.STATE,
	country: address.COUNTRY,
	postalcode: address.POSTALCODE
});

const DpmRequestDetails = props => {
	const [formData, setFormData] = useState({
		userId: '',
		emailId: '',
		firstName: '',
		lastName: '',
		companyName: '',
		taxId: '',
		propertyName: '',
		shippingAddress: {
			addressLine1: '',
			addressLine2: '',
			city: '',
			state: '',
			zipCode: ''
		},
		billingAddress: {
			addressLine1: '',
			addressLine2: '',
			city: '',
			state: '',
			zipCode: ''
		},
		phoneNumber: ''
	});
	const [dpmRequestErrorMessage, setDpmRequestErrorMessage] = useState();
	const [errors, setErrors] = useState({});
	const [dialog, setDialog] = useState();
	const dpmRequestRef = useRef();
	const requestId = props.routeParams.dpmRequestId;
	const { registration } = useSelector(({ registration }) => ({ registration }));
	const dispatch = useDispatch();
	const shippingAddressRef = useRef({});
	const billingAddressRef = useRef({});
	const [selectedShippingAddress, setSelectedShippingAddress] = useState('validated');
	shippingAddressRef.current.selected = selectedShippingAddress;
	const [selectedBillingAddress, setSelectedBillingAddress] = useState('validated');
	billingAddressRef.current.selected = selectedBillingAddress;
	const payloadRef = useRef();

	useEffect(() => {
		const populateFormData = dpmRequest => {
			dpmRequestRef.current = dpmRequest;

			setFormData({
				userId: dpmRequest.userId,
				emailId: dpmRequest.emailId,
				firstName: dpmRequest.firstName,
				lastName: dpmRequest.lastName,
				companyName: dpmRequest.companyName,
				taxId: dpmRequest.taxId,
				propertyName: dpmRequest.propertyName,
				shippingAddress: {
					addressLine1: dpmRequest.shippingAddress.addressLine1,
					addressLine2: dpmRequest.shippingAddress.addressLine2,
					city: dpmRequest.shippingAddress.city,
					state: dpmRequest.shippingAddress.state,
					zipCode: dpmRequest.shippingAddress.zipCode
				},
				billingAddress: {
					addressLine1: dpmRequest.billingAddress.addressLine1,
					addressLine2: dpmRequest.billingAddress.addressLine2,
					city: dpmRequest.billingAddress.city,
					state: dpmRequest.billingAddress.state,
					zipCode: dpmRequest.billingAddress.zipCode
				},
				phoneNumber: dpmRequest.phoneNumber
			});
			setDpmRequestErrorMessage(dpmRequest.errorMessage);
		};

		if (registration && registration.dpmRequests) {
			const dpmRequest =
				registration.dpmRequests.responseObject &&
				registration.dpmRequests.responseObject.find(dpmRequest => dpmRequest.userIdTaxId + dpmRequest.accountNumber === requestId);
			if (!['APPROVED', 'REJECT'].includes(dpmRequest.status)) {
				populateFormData(dpmRequest);
				window.scrollTo({ top: 0 });
			} else {
				browserHistory.replace('/siteManagement/dpm-requests');
			}
		} else {
			dispatch(
				genericB2BAccountRequest(`requestType=userAccountDetails&fetchType=initiated,pending,limitExceeded`, null, true, true, dpmRequests => {
					const dpmRequest = dpmRequests.responseObject && dpmRequests.responseObject.find(dpmRequest => dpmRequest.userIdTaxId + dpmRequest.accountNumber === requestId);
					if (dpmRequest) {
						populateFormData(dpmRequest);
						window.scrollTo({ top: 0 });
					} else {
						browserHistory.replace('/siteManagement/dpm-requests');
					}
				})
			);
		}
	}, [requestId]);

	const handleBack = e => {
		e && e.preventDefault();
		browserHistory.replace('/siteManagement/dpm-requests');
	};

	const validateField = (fieldName, fieldValue) => {
		fieldValue = fieldValue && fieldValue.trim();
		let errorMessage = '';
		if (['shippingAddress.addressLine2', 'billingAddress.addressLine2', 'comments'].includes(fieldName)) {
			return '';
		}
		if (!fieldValue) {
			return `${_.startCase(fieldName.split('.').at(-1))} is required`;
		}
		switch (fieldName) {
			case 'emailId':
				if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fieldValue)) {
					errorMessage = 'Invalid Email';
				}
				break;
			case 'taxId':
				if (!/^[0-9]+$/.test(fieldValue)) {
					errorMessage = 'Tax ID should be numeric';
				}
				break;
			case 'phoneNumber':
				if (!/^(?:\d{10}|\(\d{3}\)\s\d{3}-\d{4})$/.test(fieldValue)) {
					errorMessage = 'Please enter a valid US Phone Number';
				}
				break;
			case 'shippingAddress.zipCode':
			case 'billingAddress.zipCode':
				if (!/^\d{5}(?:[-\s]\d{4})?$/.test(fieldValue)) {
					errorMessage = 'Please enter a valid US Zip Code';
				}
				break;
		}
		return errorMessage;
	};

	const handleAddressSelection = (addressVerificationType, e) => {
		if (addressVerificationType === 'shippingAddressVerification') {
			setSelectedShippingAddress(e.target.value === 'asEntered' ? 'entered' : 'validated');
		} else if (addressVerificationType === 'billingAddressVerification') {
			setSelectedBillingAddress(e.target.value === 'asEntered' ? 'entered' : 'validated');
		}
	};

	const closeDialog = () => {
		setDialog(null);
	};

	const showDialog = (id, onConfirm, accountNumber) => {
		switch (id) {
			case 'confirmApproval':
				setDialog({
					id,
					title: 'Confirm',
					content: 'Are you sure you want to approve this request?',
					buttons: ['no', 'yes'],
					handleYes: () => {
						closeDialog();
						onConfirm();
					},
					onCancel: closeDialog
				});
				break;
			case 'confirmRejection':
				setDialog({
					id,
					title: 'Confirm',
					content: 'Are you sure you want to reject this request?',
					buttons: ['no', 'yes'],
					handleYes: () => {
						closeDialog();
						onConfirm();
					},
					onCancel: closeDialog
				});
				break;
			case 'success':
				setDialog({
					id,
					title: 'Approved',
					content: `Account - <b>${accountNumber}</b> has been created successfully, and the details have been sent to the customer at <b>${formData.emailId}</b>`,
					buttons: ['ok'],
					handleOk: () => {
						closeDialog();
						onConfirm();
					}
				});
				break;
		}
	};

	const handleChange = e => {
		const { name, value } = e.target;

		if (name.includes('.')) {
			const [parentKey, childKey] = name.split('.');
			setFormData(prevData => ({
				...prevData,
				[parentKey]: {
					...prevData[parentKey],
					[childKey]: value
				}
			}));
		} else {
			setFormData(prevData => ({
				...prevData,
				[name]: value
			}));
		}

		const errorMessage = validateField(name, value);
		if (errors[name] && !errorMessage) {
			setErrors(prevErrors => ({
				...prevErrors,
				[name]: ''
			}));
		}
	};

	const handleBlur = e => {
		const { name, value } = e.target;
		const updatedValue = value.trim();
		const errorMessage = validateField(name, updatedValue);

		if (name.includes('.')) {
			const [parentKey, childKey] = name.split('.');
			setFormData(prevData => ({
				...prevData,
				[parentKey]: {
					...prevData[parentKey],
					[childKey]: updatedValue
				}
			}));
		} else {
			setFormData(prevData => ({
				...prevData,
				[name]: updatedValue
			}));
		}

		if (errorMessage) {
			setErrors(prevErrors => ({
				...prevErrors,
				[name]: errorMessage
			}));
		}
	};

	const validateFormData = () => {
		const errors = {};

		const validate = (data, parentFieldName) => {
			for (const fieldName in data) {
				const fieldNamePath = `${parentFieldName ? parentFieldName + '.' : ''}${fieldName}`;
				if (typeof data[fieldName] === 'object') {
					validate(data[fieldName], fieldNamePath);
				} else {
					const errorMessage = validateField(fieldNamePath, data[fieldName]);
					if (errorMessage) {
						errors[fieldNamePath] = errorMessage;
					}
				}
			}
		};

		validate(formData);
		return errors;
	};

	const submitRequest = action => {
		dispatch(
			genericB2BAccountRequest(payloadRef.current, `requestType=${action}`, true, false, updateDpmRequestResponse => {
				if (updateDpmRequestResponse.status) {
					const updateDpmRequestResponseData = updateDpmRequestResponse.responseObject;
					if (action === 'approveUserAccount') {
						dispatch(genericB2BAccountRequest(updateDpmRequestResponseData, `requestType=consent`, false, false));
						dispatch(genericB2BAccountRequest(updateDpmRequestResponseData, `requestType=updateAccount`, false, false));
					}
					const updatedDpmRequests = JSON.parse(JSON.stringify(registration.dpmRequests));
					updatedDpmRequests.responseObject = updatedDpmRequests.responseObject.map(dpmRequest =>
						dpmRequest.userIdTaxId + dpmRequest.accountNumber === requestId
							? { ...dpmRequest, status: action === 'approveUserAccount' ? 'APPROVED' : 'REJECT' }
							: dpmRequest
					);
					dispatch(saveDPMRequests(updatedDpmRequests));
					if (action === 'approveUserAccount') {
						showDialog(
							'success',
							() => handleBack(),
							updateDpmRequestResponseData.customerAccountResponse && updateDpmRequestResponseData.customerAccountResponse.X_ACCOUNT_NUMBER
						);
					} else {
						handleBack();
					}
				} else {
					browserHistory.push('/error');
				}
			})
		);
	};

	const convertToMuleValidationResult = (enteredAddress, validatedAddress) => ({
		ADDRESS_LINE1: enteredAddress.addressLine1,
		ADDRESS_LINE2: enteredAddress.addressLine2,
		CITY: enteredAddress.city,
		COUNTY: validatedAddress.COUNTY,
		STATE: enteredAddress.state,
		POSTALCODE: enteredAddress.zipCode,
		COUNTRY: validatedAddress.COUNTRY
	});

	const isPoOrHcOrPmbAddress = address => {
		const poOrHcBoxRegex = /\b(?:P(\.?O\.?|OST)\s*BOX|HC\s*\d+\s*BOX\s*\d+|PMB\s*\d+)\b/i;
		return poOrHcBoxRegex.test(address.addressLine1) || poOrHcBoxRegex.test(address.addressLine2 || '');
	};

	const isSubmissionAllowed = () => {
		if (
			isPoOrHcOrPmbAddress(shippingAddressRef.current.entered) ||
			(shippingAddressRef.current.errors && shippingAddressRef.current.errors.length) ||
			(billingAddressRef.current.errors && billingAddressRef.current.errors.some(error => !ALLOWED_BILLING_ADDRESS_VALIDATION_ERROR_CODES.includes(error.code)))
		) {
			return false;
		}
		return true;
	};

	const showShippingAddressVerificationDialog = () => {
		setDialog({
			id: 'shippingAddressVerification',
			title: 'Verify Shipping Address',
			enteredAddress: formData.shippingAddress,
			suggestedAddress: { responseObject: convertMuleToSuggestedAddress(shippingAddressRef.current.validated) },
			errorMessage: shippingAddressRef.current.errorMessage,
			messages: shippingAddressRef.current.messages,
			buttons: [
				{ type: 'cancel', label: 'Back', onClick: closeDialog },
				{ type: 'confirm', label: 'Continue', onClick: showBillingAddressVerificationDialog }
			]
		});
	};

	const showBillingAddressVerificationDialog = () => {
		setDialog({
			id: 'billingAddressVerification',
			title: 'Verify Billing Address',
			enteredAddress: formData.billingAddress,
			suggestedAddress: { responseObject: convertMuleToSuggestedAddress(billingAddressRef.current.validated) },
			errorMessage: billingAddressRef.current.errorMessage,
			messages: billingAddressRef.current.messages,
			buttons: [
				{ type: 'cancel', label: 'Back', onClick: showShippingAddressVerificationDialog },
				{
					type: 'confirm',
					label: 'Submit',
					onClick: () => {
						payloadRef.current = {
							...payloadRef.current,
							shippingAddressValidationResult:
								shippingAddressRef.current.selected === 'entered'
									? convertToMuleValidationResult(formData.shippingAddress, shippingAddressRef.current.validated)
									: shippingAddressRef.current.validated,
							billingAddressValidationResult:
								billingAddressRef.current.selected === 'entered'
									? convertToMuleValidationResult(formData.billingAddress, billingAddressRef.current.validated)
									: billingAddressRef.current.validated
						};
						console.log('PAYLOAD: ', payloadRef.current);
						submitRequest('approveUserAccount');
					}
					// disabled: !isSubmissionAllowed()
				}
			]
		});
	};

	const validateAddressResponse = (addressType, addressResponse) => {
		const addressValidationErrorsWithTargets = [];
		const melissaAddressValidationErrors = addressType === 'shippingAddress' ? SHIPPING_ADDRESS_VALIDATION_ERRORS : BILLING_ADDRESS_VALIDATION_ERRORS;
		const melissaAddressValidationErrorCodesFromApi = addressResponse.RESULTS.split(',');

		melissaAddressValidationErrors.forEach(melissaAddressValidationError => {
			const isAnErrorCode = melissaAddressValidationErrorCodesFromApi.some(
				melissaAddressValidationErrorCodeFromApi => melissaAddressValidationErrorCodeFromApi === melissaAddressValidationError.code
			);

			if (isAnErrorCode) {
				addressValidationErrorsWithTargets.push({
					...melissaAddressValidationError
					// targets: getTargetsForAddressErrorCode(addressType, melissaAddressValidationError.code)
				});
			}
		});

		return addressValidationErrorsWithTargets;
	};

	const validateAddress = () => {
		setSelectedShippingAddress('validated');
		setSelectedBillingAddress('validated');

		dispatch(ajaxRequestInitiated());
		const shippingAddressValidationPromise = sendRequest(
			routes.b2bPortalAPIUrl,
			routes.users.b2bAccounts.path,
			'',
			'GET',
			`requestType=validateAddress&addressLine1=${formData.shippingAddress.addressLine1}&addressLine2=${formData.shippingAddress.addressLine2}&city=${formData.shippingAddress.city}&state=${formData.shippingAddress.state}&zip=${formData.shippingAddress.zipCode}&country=US`
		);
		const billingAddressValidationPromise = sendRequest(
			routes.b2bPortalAPIUrl,
			routes.users.b2bAccounts.path,
			'',
			'GET',
			`requestType=validateAddress&addressLine1=${formData.billingAddress.addressLine1}&addressLine2=${formData.billingAddress.addressLine2}&city=${formData.billingAddress.city}&state=${formData.billingAddress.state}&zip=${formData.billingAddress.zipCode}&country=US`
		);

		Promise.all([shippingAddressValidationPromise, billingAddressValidationPromise])
			.then(([shippingAddressValidationResponseData, billingAddressValidationResponseData]) => {
				if (shippingAddressValidationResponseData.responseObject && billingAddressValidationResponseData.responseObject) {
					shippingAddressRef.current.errors = validateAddressResponse('shippingAddress', shippingAddressValidationResponseData.responseObject);
					shippingAddressRef.current.errorMessage = '';
					shippingAddressRef.current.messages = [];

					billingAddressRef.current.errors = validateAddressResponse('billingAddress', billingAddressValidationResponseData.responseObject);
					billingAddressRef.current.errorMessage;
					billingAddressRef.current.messages = [];

					shippingAddressRef.current.entered = {
						...formData.shippingAddress
					};
					shippingAddressRef.current.validated = shippingAddressValidationResponseData.responseObject;
					if (isPoOrHcOrPmbAddress(shippingAddressRef.current.entered)) {
						shippingAddressRef.current.errorMessage = 'A PO/HC Box cannot be used as a Shipping Address, please provide a valid Shipping Address';
					}
					if (!shippingAddressRef.current.errorMessage && shippingAddressRef.current.errors.length) {
						shippingAddressRef.current.errorMessage = 'This is an invalid address';
						shippingAddressRef.current.errors.forEach(error => {
							if (!shippingAddressRef.current.messages.includes(error.message)) {
								shippingAddressRef.current.messages.push(error.message);
							}
						});
					}

					billingAddressRef.current.entered = {
						...formData.billingAddress
					};
					billingAddressRef.current.validated = billingAddressValidationResponseData.responseObject;
					if (billingAddressRef.current.errors.length) {
						billingAddressRef.current.errorMessage = 'This is an invalid address';
						billingAddressRef.current.errors.forEach(error => {
							if (!billingAddressRef.current.messages.includes(error.message)) {
								billingAddressRef.current.messages.push(error.message);
							}
						});
					}
					showShippingAddressVerificationDialog();
				} else {
					browserHistory.push('/error');
				}
			})
			.finally(() => dispatch(ajaxRequestCompleted()));
	};

	const updateRequest = action => {
		payloadRef.current = {
			...dpmRequestRef.current,
			...formData,
			shippingAddress: {
				...dpmRequestRef.current.shippingAddress,
				...formData.shippingAddress
			},
			billingAddress: {
				...dpmRequestRef.current.billingAddress,
				...formData.billingAddress
			}
		};

		if (action === 'approveUserAccount') {
			validateAddress();
		} else {
			submitRequest(action);
		}
	};

	const handleUpdate = (action, e) => {
		e.preventDefault();

		if (action === 'rejectUserAccount') {
			showDialog('confirmRejection', updateRequest.bind(this, action));
		} else {
			const newErrors = validateFormData();

			if (Object.keys(newErrors).length > 0) {
				setErrors(newErrors);
				return;
			}
			showDialog('confirmApproval', updateRequest.bind(this, action));
		}
	};

	return (
		<React.Fragment>
			<div className="dpmRequestDetails">
				<div className="push">
					<h1 className="pageTitle">Review DPM Request</h1>
				</div>
				<form className="newPropertyForm">
					{dpmRequestErrorMessage && (
						<section className="red push">
							<b>Errors: </b>
							<ul style={{ listStyle: 'none' }}>
								{dpmRequestErrorMessage
									.split(';')
									.filter(Boolean)
									.map(errorMessage => (
										<li key={errorMessage}>{errorMessage}</li>
									))}
							</ul>
						</section>
					)}
					<section className="newPropertyFormGrid push">
						<div className="newPropertyFormFieldGroup">
							<label htmlFor="userId">
								<b>User ID</b>
							</label>
							<div className="input-wrapper">
								<input
									id="userId"
									type="text"
									className="newPropertyFormInput"
									name="userId"
									value={formData.userId}
									onChange={handleChange}
									onBlur={handleBlur}
									disabled
								/>
							</div>
						</div>
						<div className="newPropertyFormFieldGroup">
							<label htmlFor="emailId">
								<b>Email</b>
							</label>
							<div className="input-wrapper">
								<input
									id="emailId"
									className="newPropertyFormInput"
									type="text"
									name="emailId"
									value={formData.emailId}
									onChange={handleChange}
									onBlur={handleBlur}
									disabled
								/>
							</div>
						</div>
						<div className="newPropertyFormFieldGroup">
							<label htmlFor="firstName">
								<b>First Name</b>
							</label>
							<div className="input-wrapper">
								<input
									id="firstName"
									type="text"
									className="newPropertyFormInput"
									name="firstName"
									value={formData.firstName}
									onChange={handleChange}
									onBlur={handleBlur}
									disabled
								/>
							</div>
						</div>
						<div className="newPropertyFormFieldGroup">
							<label htmlFor="lastName">
								<b>Last Name</b>
							</label>
							<div className="input-wrapper">
								<input
									id="lastName"
									type="text"
									className="newPropertyFormInput"
									name="lastName"
									value={formData.lastName}
									onChange={handleChange}
									onBlur={handleBlur}
									disabled
								/>
							</div>
						</div>
						<div className="newPropertyFormFieldGroup">
							<label htmlFor="taxId">
								<b>Tax ID</b>
							</label>
							<div className="input-wrapper">
								<input id="taxId" type="text" className="newPropertyFormInput" name="taxId" value={formData.taxId} onChange={handleChange} onBlur={handleBlur} />
								{errors.taxId && (
									<p className="small red my-0 text-capitalize">
										<small>{errors.taxId}</small>
									</p>
								)}
							</div>
						</div>
						<div className="newPropertyFormFieldGroup">
							<label htmlFor="companyName">
								<b>Company Name</b>
							</label>
							<div className="input-wrapper">
								<input
									id="companyName"
									type="text"
									className="newPropertyFormInput"
									name="companyName"
									value={formData.companyName}
									onChange={handleChange}
									onBlur={handleBlur}
								/>
								{errors.companyName && (
									<p className="small red my-0 text-capitalize">
										<small>{errors.companyName}</small>
									</p>
								)}
							</div>
						</div>
						<div className="newPropertyFormFieldGroup">
							<label htmlFor="propertyName">
								<b>Property Name</b>
							</label>
							<div className="input-wrapper">
								<input
									id="propertyName"
									type="text"
									className="newPropertyFormInput"
									name="propertyName"
									value={formData.propertyName}
									onChange={handleChange}
									onBlur={handleBlur}
								/>
								{errors.propertyName && (
									<p className="small red my-0 text-capitalize">
										<small>{errors.propertyName}</small>
									</p>
								)}
							</div>
						</div>
						<div className="newPropertyFormFieldGroup">
							<label htmlFor="shippingAddress.addressLine1">
								<b>Shipping Address Line 1</b>
							</label>
							<div className="input-wrapper">
								<input
									id="shippingAddress.addressLine1"
									type="text"
									className="newPropertyFormInput"
									name="shippingAddress.addressLine1"
									value={formData.shippingAddress.addressLine1}
									onChange={handleChange}
									onBlur={handleBlur}
								/>
								{errors['shippingAddress.addressLine1'] && (
									<p className="small red my-0 text-capitalize">
										<small>{errors['shippingAddress.addressLine1']}</small>
									</p>
								)}
							</div>
						</div>
						<div className="newPropertyFormFieldGroup">
							<label htmlFor="shippingAddress.addressLine2">
								<b>Shipping Address Line 2</b>
							</label>
							<div className="input-wrapper">
								<input
									id="shippingAddress.addressLine2"
									type="text"
									className="newPropertyFormInput"
									name="shippingAddress.addressLine2"
									value={formData.shippingAddress.addressLine2}
									onChange={handleChange}
									onBlur={handleBlur}
								/>
								{errors['shippingAddress.addressLine2'] && (
									<p className="small red my-0 text-capitalize">
										<small>{errors['shippingAddress.addressLine2']}</small>
									</p>
								)}
							</div>
						</div>
						<div className="newPropertyFormFieldGroup">
							<label htmlFor="shippingAddress.city">
								<b>City</b>
							</label>
							<div className="input-wrapper">
								<input
									id="shippingAddress.city"
									type="text"
									className="newPropertyFormInput"
									name="shippingAddress.city"
									value={formData.shippingAddress.city}
									onChange={handleChange}
									onBlur={handleBlur}
								/>
								{errors['shippingAddress.city'] && (
									<p className="small red my-0 text-capitalize">
										<small>{errors['shippingAddress.city']}</small>
									</p>
								)}
							</div>
						</div>
						<div className="addNewPropertyFormSplitGroup">
							<div className="newPropertyFormSplitItem">
								<label htmlFor="shippingAddress.state">
									<b>State</b>
								</label>
								<div>
									<select
										id="shippingAddress.state"
										className="newPropertyFormSelect"
										name="shippingAddress.state"
										value={formData.shippingAddress.state}
										onChange={handleChange}
										onBlur={handleBlur}
									>
										<option value="" disabled>
											Please Select
										</option>
										{US_CONTIGUOUS_STATES.map(state => (
											<option key={state.code} value={state.code}>
												{state.name}
											</option>
										))}
									</select>
									{errors['shippingAddress.state'] && (
										<p className="small red my-0 text-capitalize">
											<small>{errors['shippingAddress.state']}</small>
										</p>
									)}
								</div>
							</div>
							<div className="newPropertyFormSplitItem">
								<label htmlFor="shippingAddress.zipCode">
									<b>Zip Code</b>
								</label>
								<div>
									<input
										id="shippingAddress.zipCode"
										type="text"
										className="newPropertyFormInput"
										inputMode="numeric"
										name="shippingAddress.zipCode"
										value={formData.shippingAddress.zipCode}
										onChange={handleChange}
										onBlur={handleBlur}
									/>
									{errors['shippingAddress.zipCode'] && (
										<p className="small red my-0 text-capitalize">
											<small>{errors['shippingAddress.zipCode']}</small>
										</p>
									)}
								</div>
							</div>
						</div>
						<div className="newPropertyFormFieldGroup">
							<label htmlFor="billingAddress.addressLine1">
								<b>Billing Address Line 1</b>
							</label>
							<div className="input-wrapper">
								<input
									id="billingAddress.addressLine1"
									type="text"
									className="newPropertyFormInput"
									name="billingAddress.addressLine1"
									value={formData.billingAddress.addressLine1}
									onChange={handleChange}
									onBlur={handleBlur}
								/>
								{errors['billingAddress.addressLine1'] && (
									<p className="small red my-0 text-capitalize">
										<small>{errors['billingAddress.addressLine1']}</small>
									</p>
								)}
							</div>
						</div>
						<div className="newPropertyFormFieldGroup">
							<label htmlFor="billingAddress.addressLine2">
								<b>Billing Address Line 2</b>
							</label>
							<div className="input-wrapper">
								<input
									id="billingAddress.addressLine2"
									type="text"
									className="newPropertyFormInput"
									name="billingAddress.addressLine2"
									value={formData.billingAddress.addressLine2}
									onChange={handleChange}
									onBlur={handleBlur}
								/>
								{errors['billingAddress.addressLine2'] && (
									<p className="small red my-0 text-capitalize">
										<small>{errors['billingAddress.addressLine2']}</small>
									</p>
								)}
							</div>
						</div>
						<div className="newPropertyFormFieldGroup">
							<label htmlFor="billingAddress.city">
								<b>City</b>
							</label>
							<div className="input-wrapper">
								<input
									id="billingAddress.city"
									type="text"
									className="newPropertyFormInput"
									name="billingAddress.city"
									value={formData.billingAddress.city}
									onChange={handleChange}
									onBlur={handleBlur}
								/>
								{errors['billingAddress.city'] && (
									<p className="small red my-0 text-capitalize">
										<small>{errors['billingAddress.city']}</small>
									</p>
								)}
							</div>
						</div>
						<div className="addNewPropertyFormSplitGroup">
							<div className="newPropertyFormSplitItem">
								<label htmlFor="billingAddress.state">
									<b>State</b>
								</label>
								<div>
									<select
										id="billingAddress.state"
										className="newPropertyFormSelect"
										name="billingAddress.state"
										value={formData.billingAddress.state}
										onChange={handleChange}
										onBlur={handleBlur}
									>
										<option value="" disabled>
											Please Select
										</option>
										{US_CONTIGUOUS_STATES.map(state => (
											<option key={state.code} value={state.code}>
												{state.name}
											</option>
										))}
									</select>
									{errors['billingAddress.state'] && (
										<p className="small red my-0 text-capitalize">
											<small>{errors['billingAddress.state']}</small>
										</p>
									)}
								</div>
							</div>
							<div className="newPropertyFormSplitItem">
								<label htmlFor="billingAddress.zipCode">
									<b>Zip Code</b>
								</label>
								<div>
									<input
										id="billingAddress.zipCode"
										type="text"
										className="newPropertyFormInput"
										inputMode="numeric"
										name="billingAddress.zipCode"
										value={formData.billingAddress.zipCode}
										onChange={handleChange}
										onBlur={handleBlur}
									/>
									{errors['billingAddress.zipCode'] && (
										<p className="small red my-0 text-capitalize">
											<small>{errors['billingAddress.zipCode']}</small>
										</p>
									)}
								</div>
							</div>
						</div>
						<div className="newPropertyFormFieldGroup">
							<label htmlFor="phoneNumber">
								<b>Phone</b>
							</label>
							<div className="input-wrapper">
								<input
									id="phoneNumber"
									type="text"
									className="newPropertyFormInput"
									name="phoneNumber"
									value={formData.phoneNumber}
									onChange={handleChange}
									onBlur={handleBlur}
								/>
								{errors.phoneNumber && (
									<p className="small red my-0 text-capitalize">
										<small>{errors.phoneNumber}</small>
									</p>
								)}
							</div>
						</div>
						<div className="newPropertyFormFieldGroup">
							<label htmlFor="comments">
								<b>Comments</b>
							</label>
							<div className="input-wrapper">
								<textarea
									id="comments"
									type="text"
									className="newPropertyFormInput"
									name="comments"
									value={formData.comments}
									onChange={handleChange}
									onBlur={handleBlur}
								/>
							</div>
						</div>
					</section>
					<p className="newPropertyFormSubmitContainer flex justify-between push">
						<button type="button" className="orangeButton" onClick={handleUpdate.bind(this, 'rejectUserAccount')}>
							Reject
						</button>
						<button type="button" onClick={handleUpdate.bind(this, 'approveUserAccount')}>
							Save & Approve
						</button>
					</p>
					<p>
						<a href="" onClick={handleBack}>
							Return to Previous Screen
						</a>
					</p>
				</form>
			</div>
			{dialog &&
				(['shippingAddressVerification', 'billingAddressVerification'].includes(dialog.id) ? (
					<AddressVerificationDialog
						key={dialog.id}
						title={dialog.title}
						enteredAddress={dialog.enteredAddress}
						suggestedAddress={dialog.suggestedAddress}
						isEnteredAddressCorrect={dialog.id === 'shippingAddressVerification' ? selectedShippingAddress === 'entered' : selectedBillingAddress === 'entered'}
						onAddressSelection={handleAddressSelection.bind(this, dialog.id)}
						errorMessage={dialog.errorMessage}
						messages={dialog.messages}
						onClose={closeDialog}
						buttons={dialog.buttons}
					/>
				) : (
					<GenericOverlayDialog
						flag={dialog.id}
						genericDialogHeading={dialog.title}
						genericDialogText={dialog.content}
						buttons={dialog.buttons}
						handleYes={dialog.handleYes}
						showDialog
						defaultTop
						handleDialog={closeDialog}
						handleOk={dialog.handleOk}
					/>
				))}
		</React.Fragment>
	);
};

export default DpmRequestDetails;



// WEBPACK FOOTER //
// ./src/App/components/PostLogin/SiteManagement/Dpm/dpmRequestDetails.jsx